/* eslint-disable react-hooks/exhaustive-deps */

// React
import { useEffect, useState } from "react"

// Components
import { Card, FooterMenu, Pagination } from "components"
import { DropdownAccount } from "../DropdownAccount"
import { FilterByDate } from "../FilterByDate"
import { FooterSummary } from "../FooterSummary"
import { TableRegister } from "./components"

// Contexts
import { RowIndexContext } from "../../contexts"

// Third-Party Libraries
import moment from "moment"

// Types
import type { SuccessFetch } from "types"
import type { PaginationType, RegisterDateType } from "../../types"

// Utils
import { useApi } from "utils"
import { useFilter } from "pages/Finance/Journal/utils/hooks"
import {
  exportPdf,
  prevYear,
  thisMonth,
  thisWeek,
  thisYear,
  today
} from "utils/functions"
import { usePagination } from "../../utils/hooks"
import { ExportExcel } from "../ExportExcel"
import { ExportPdf } from "../ExportPdf"
import { PERMISION } from "security"

type Response = {
  payload: {
    beginingbalance: number
    currentbalance: number
    data_register: RegisterDateType[]
    pagination: PaginationType
  }
}

export function RegisterDateRange() {
  const api = useApi()
  const todayDate = today()
  const prevYearDate = prevYear()
  const thisYearDate = thisYear()
  const thisMonthDate = thisMonth()
  const thisWeekDate = thisWeek()
  const {
    currentPage,
    limit,
    size,
    totalPage,
    total,
    getRowNumber,
    onChangeLimit,
    onChangePage,
    onChangePaginationData,
    resetPage
  } = usePagination()
  const [balance, setBalance] = useState({ beginning: 0, current: 0 })
  const [data, setData] = useState<RegisterDateType[]>([])
  const [isLoading, setLoading] = useState(false)

  const { filter, handleChangeFilter } = useFilter({
    CoaID: "0",
    dateFrom: thisMonthDate.fromDate,
    dateTo: todayDate
  })

  const fetch = () => {
    setLoading(true)

    api
      .postForm(`/regbydatarange/getdata?page=${currentPage}&limit=${limit}`, {
        CoaID: filter.CoaID,
        StartDate: filter.dateFrom,
        EndDate: filter.dateTo
      })
      .then((res: SuccessFetch<Response>) => {
        const data = res.data.payload

        onChangePaginationData(data.pagination)

        setBalance({
          beginning: data.beginingbalance,
          current: data.currentbalance
        })

        setData(
          data.data_register?.map((item) => ({
            ...item,
            checked: false
          })) ?? []
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    resetPage()

    if (filter.CoaID) {
      fetch()
    }
  }, [filter, limit])

  useEffect(() => {
    fetch()
  }, [currentPage])

  const handleCheckData = (index: number) => {
    const newData = [...data]
    data[index].checked = !data[index].checked

    setData(newData)
  }

  return (
    <section className="flex flex-col gap-2 mt-2">
      <Card className="bg-white">
        <Card.Body>
          <section className="container flex-col lg:flex lg:flex-row justify-center lg:justify-between">
            <div className="w-full lg:w-1/2 flex-col lg:flex lg:flex-row justify-center lg:justify-start items-center">
              <div className="label-text pb-1 font-bold pe-6">ACCOUNT</div>
              <DropdownAccount
                value={null}
                currentBalance={balance.current ?? 0}
                onChange={(item) => handleChangeFilter({ CoaID: item?.value })}
              />
            </div>
          </section>
          <FilterByDate
            permission={PERMISION.FINANCE.REGISTER.BY_DATE_RANGE.PERMISION}
            dateFrom={filter.dateFrom}
            dateTo={filter.dateTo}
            onChangeDateFrom={(dateFrom) => {
              const isValid = moment(dateFrom).isBefore(filter.dateTo)
              handleChangeFilter({
                dateFrom,
                dateTo: isValid ? filter.dateTo : dateFrom
              })
            }}
            onChangeDateTo={(dateTo) => handleChangeFilter({ dateTo })}
            onClickPrevYear={() =>
              handleChangeFilter({
                dateFrom: prevYearDate.fromDate,
                dateTo: prevYearDate.toDate
              })
            }
            onClickThisYear={() =>
              handleChangeFilter({
                dateFrom: thisYearDate.fromDate,
                dateTo: thisYearDate.toDate
              })
            }
            onClickThisWeek={() =>
              handleChangeFilter({
                dateFrom: thisWeekDate.fromDate,
                dateTo: thisWeekDate.toDate
              })
            }
            onClickThisMonth={() =>
              handleChangeFilter({
                dateFrom: thisMonthDate.fromDate,
                dateTo: thisMonthDate.toDate
              })
            }
            onClickToday={() =>
              handleChangeFilter({
                dateFrom: todayDate,
                dateTo: todayDate
              })
            }
          />
        </Card.Body>
      </Card>

      <div className="overflow-x-scroll max-h-[calc(100vh-500px)]">
        <RowIndexContext.Provider value={getRowNumber}>
          <TableRegister
            data={data}
            filter={filter}
            isLoading={isLoading}
            handleCheckData={handleCheckData}
            fetchList={fetch}
            permission={PERMISION.FINANCE.REGISTER.BY_DATE_RANGE.PERMISION}
          />
        </RowIndexContext.Provider>
        {Boolean(!isLoading && data.length) && (
          <div className="mb-2">
            <Pagination
              currentPage={currentPage}
              totalPage={totalPage}
              size={size}
              limit={limit}
              total={total}
              onPageChange={onChangePage}
              onAddShow={(res) => onChangeLimit(Number(res))}
            />
          </div>
        )}

        <FooterMenu
          customRightElement={
            <FooterSummary data={data} dateFrom={filter.dateFrom} />
          }
          export={{
            actiontype: "export",
            onClick: () => {
              ExportExcel({
                data: data,
                filter: {
                  dateFrom: filter.dateFrom,
                  dateTo: filter.dateTo
                },
                fileName: "finance_register_by_date_range",
                title: "FINANCE REGISTER BY DATE RANGE"
              })
            }
          }}
          pdf={{
            actiontype: "pdf",
            onClick: () => {
              exportPdf({
                documentComponent: (
                  <ExportPdf
                    data={data}
                    filter={{
                      dateFrom: filter.dateFrom,
                      dateTo: filter.dateTo
                    }}
                    title="FINANCE REGISTER BY DATE RANGE"
                  />
                ),
                fileName: "finance_register_by_date_range"
              })
            }
          }}
        />
      </div>
    </section>
  )
}
